import {
    Box,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { AccountBalanceWallet, Assessment, CreditCard, LocalShipping } from "@material-ui/icons"
import { VERDE } from "./Filtros"
import { formatarMoeda } from "./constants"

const useStyles = makeStyles({
    card: {
        backgroundColor: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        height: "100%",
        overflow: "hidden",
    },
    cabecalho: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "14px 18px",
        borderBottom: "1px solid #f0f0f0",
        "& svg": { color: VERDE },
    },
    tituloCard: { fontWeight: 700, color: "#1a1f36", fontSize: 15 },
    corpo: { padding: "12px 18px 16px" },
    linha: {
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        padding: "5px 0",
        borderBottom: "1px dashed #f0f0f0",
        "&:last-child": { borderBottom: "none" },
    },
    rotulo: { color: "#6b7280", fontSize: 14 },
    valor: { color: "#1a1f36", fontWeight: 600, fontSize: 14, whiteSpace: "nowrap" },
    destaque: {
        "& $rotulo": { color: "#1a1f36", fontWeight: 600 },
        "& $valor": { color: "#1b7a34", fontSize: 16 },
    },
    tabelaCabecalho: {
        "& th": {
            color: "#6b7280",
            fontSize: 11,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            whiteSpace: "nowrap",
        },
    },
})

const percentual = v => `${(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`

const Cartao = ({ icone, titulo, children }) => {
    const classes = useStyles()
    return (
        <Paper elevation={0} className={classes.card}>
            <Box className={classes.cabecalho}>
                {icone}
                <Typography className={classes.tituloCard}>{titulo}</Typography>
            </Box>
            <Box className={classes.corpo}>{children}</Box>
        </Paper>
    )
}

const Linhas = ({ itens }) => {
    const classes = useStyles()
    return itens.map(([rotulo, valor, destaque]) => (
        <Box key={rotulo} className={`${classes.linha} ${destaque ? classes.destaque : ""}`}>
            <span className={classes.rotulo}>{rotulo}</span>
            <span className={classes.valor}>{valor}</span>
        </Box>
    ))
}

/**
 * Seção de resumos do Histórico de Pedidos (mesmos cartões do antigo Relatório de Pedidos),
 * calculada no backend sobre TODOS os pedidos do filtro — não só a página visível.
 */
export default function ResumoPedidos({ dados, carregando, erro }) {
    const classes = useStyles()

    if (carregando && !dados) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" py={4} style={{ gap: 12 }}>
                <CircularProgress size={22} style={{ color: VERDE }} />
                <Typography variant="body2" style={{ color: "#6b7280" }}>
                    Calculando o resumo…
                </Typography>
            </Box>
        )
    }
    if (erro) {
        return (
            <Box py={2}>
                <Typography variant="body2" style={{ color: "#991b1b" }}>
                    {erro}
                </Typography>
            </Box>
        )
    }
    if (!dados) return null

    const { resumo, cartaoOnline, pixOnline, formasPagamento = [], entregadores = [] } = dados

    return (
        <Box mt={3} style={{ opacity: carregando ? 0.6 : 1, transition: "opacity 0.2s" }}>
            <Typography style={{ fontWeight: 700, fontSize: 20, color: "#1a1f36", marginBottom: 12 }}>
                Resumo do período
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Cartao icone={<Assessment />} titulo="Resumo">
                        <Linhas
                            itens={[
                                ["(+) Valor bruto", formatarMoeda(resumo.valorBruto)],
                                ["(-) Descontos", formatarMoeda(resumo.descontos)],
                                ["(=) Valor líquido", formatarMoeda(resumo.liquido), true],
                                ["(%) Percentual de descontos", percentual(resumo.percentualDesconto)],
                                ["Total de taxas de entrega", formatarMoeda(resumo.taxasEntrega)],
                                [
                                    `Cancelados/expirados (${resumo.qtdCancelados})`,
                                    formatarMoeda(resumo.perdidos),
                                ],
                            ]}
                        />
                    </Cartao>
                </Grid>

                {cartaoOnline?.pagamentos > 0 && (
                    <Grid item xs={12} md={4}>
                        <Cartao icone={<CreditCard />} titulo="Cartão Online">
                            <Linhas
                                itens={[
                                    ["Pagamentos online", formatarMoeda(cartaoOnline.pagamentos)],
                                    ["Taxa", percentual(cartaoOnline.taxa)],
                                    [
                                        `Chargeback (${percentual(cartaoOnline.chargeBackPercentual)})`,
                                        formatarMoeda(cartaoOnline.chargeback),
                                    ],
                                    ["Saldo", formatarMoeda(cartaoOnline.saldo), true],
                                ]}
                            />
                        </Cartao>
                    </Grid>
                )}

                <Grid item xs={12} md={4}>
                    <Cartao icone={<AccountBalanceWallet />} titulo="Pix Online">
                        <Linhas
                            itens={[
                                ["Qtd. de vendas Pix", String(pixOnline?.qtd ?? 0)],
                                ["Valor das vendas Pix", formatarMoeda(pixOnline?.valor)],
                                ["Taxas Pix", formatarMoeda(pixOnline?.taxas)],
                                ["Líquido Pix", formatarMoeda(pixOnline?.liquido), true],
                            ]}
                        />
                    </Cartao>
                </Grid>

                {formasPagamento.length > 0 && (
                    <Grid item xs={12} md={6}>
                        <Cartao icone={<AccountBalanceWallet />} titulo="Formas de Pagamento">
                            <Linhas
                                itens={formasPagamento.map(fp => [
                                    `${fp.nome} (${fp.qtd} ${fp.qtd === 1 ? "pedido" : "pedidos"})`,
                                    formatarMoeda(fp.valor),
                                ])}
                            />
                        </Cartao>
                    </Grid>
                )}

                {entregadores.length > 0 && (
                    <Grid item xs={12} md={6}>
                        <Cartao
                            icone={<LocalShipping />}
                            titulo={`Resumo de Entregas (${resumo.qtdTotalEntregas} pedidos)`}
                        >
                            <TableContainer style={{ overflowX: "auto" }}>
                                <Table size="small" style={{ minWidth: 480 }}>
                                    <TableHead className={classes.tabelaCabecalho}>
                                        <TableRow>
                                            <TableCell>Entregador</TableCell>
                                            <TableCell align="right">Vr. total</TableCell>
                                            <TableCell align="right">Qtd.</TableCell>
                                            <TableCell align="right">Dinheiro</TableCell>
                                            <TableCell align="right">Cartão</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {entregadores.map(ent => (
                                            <TableRow key={ent.nome}>
                                                <TableCell>{ent.nome}</TableCell>
                                                <TableCell align="right">{formatarMoeda(ent.vrTotal)}</TableCell>
                                                <TableCell align="right">{ent.qtdEntregas}</TableCell>
                                                <TableCell align="right">{formatarMoeda(ent.vrDinheiro)}</TableCell>
                                                <TableCell align="right">{formatarMoeda(ent.vrCartao)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Cartao>
                    </Grid>
                )}
            </Grid>
        </Box>
    )
}
